import { ListboxOption } from "@reach/listbox";
import { VisuallyHidden } from "@reach/visually-hidden";
import classNames from "classnames";
import Clickable from "components/Clickable";
import { ButtonPrimary, ButtonText } from "components/Clickable/Buttons";
import { OptionalCta } from "components/Clickable/Cta";
import { CmsResponsiveImage } from "components/CmsResponsiveImage";
import { Dropdown } from "components/Dropdown/Dropdown";
import { FormLabel } from "components/Input/FormLabel";
import { SearchInput } from "components/Input/Input";
import { useRouterParams } from "components/useRouterParams";
import {
    OnlineDealerBlock,
    OnlineDealersBlock,
} from "components/wagtail/DealerLocatorPage";
import GoogleMapReact from "google-map-react";
import {
    formatPhoneNumber,
    useIsClient,
    useIsMobile,
    useWindowWidth,
} from "helpers/utils";
import React, {
    ChangeEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import Directions from "../../public/svg/directions.svg";
import FeaturedPin from "../../public/svg/featured-pin.svg";
import FeaturedStar from "../../public/svg/featured-star.svg";
import Globe from "../../public/svg/globe.svg";
import MapWithPins from "../../public/svg/map-with-pins.svg";
import DealerCart from "../../public/svg/online-dealers.svg";
import Pin from "../../public/svg/pin.svg";
import { Dealer } from "./DealerLocator.interfaces";
import { googleApiKey } from "./env";

import sassVariables from "../../styles/variables.module.scss";
import headerStyles from "./dealer-header.module.scss";
import listStyles from "./dealer-list.module.scss";
import styles from "./dealer-locator.module.scss";
import mapStyles from "./dealer-map.module.scss";

interface DealerListProps {
    dealers: Dealer[];
    selectedDealerID: number;
    setSelectedDealerID: (dealerId: number) => void;
}

const METERS_PER_MILE = 1_609.344;

function metersToMiles(meters: number) {
    const miles = meters / METERS_PER_MILE;
    return Math.round(miles * 100) / 100;
}

export const DealerList = (props: DealerListProps) => {
    const buildDirectionsURI = (dealer: Dealer) => {
        const destinationAddress = encodeURI(
            [
                dealer.address,
                dealer.address2,
                dealer.city,
                dealer.state,
                dealer.postal,
            ].join(" "),
        );
        return `https://www.google.com/maps/dir/?api=1&destination=${destinationAddress}`;
    };
    if (props.dealers.length === 0) {
        return (
            <p className={styles.noResultsMessage}>
                We're sorry, but we can't find any locations.
                <br /> Please try searching in a different area.
            </p>
        );
    }
    return (
        <ul id="dealer-list" className={listStyles.root}>
            {props.dealers.map((dealer, i) => {
                const attributes = [];
                if (dealer.displays_door_hardware_emtek)
                    attributes.push("Door Hardware on Display");
                if (dealer.displays_cabinet_hardware_emtek)
                    attributes.push("Cabinet Hardware on Display");
                if (dealer.stocks_cabinet_hardware_emtek)
                    attributes.push("Cabinet Hardware Stocking Dealer");

                const PinComponent = dealer.is_featured_emtek
                    ? FeaturedPin
                    : Pin;

                return (
                    <li
                        key={dealer.id}
                        id={`dealer-list-item-${dealer.id}`}
                        onClick={() => {
                            props.setSelectedDealerID(dealer.id);
                        }}
                        className={classNames({
                            [listStyles.item]: true,
                            [listStyles.selectedItem]:
                                dealer.id === props.selectedDealerID,
                            "al-dealers__dealer-item": true,
                        })}
                    >
                        <div className={listStyles.markerWrapper}>
                            <PinComponent
                                className={classNames({
                                    [listStyles.selectedPin]:
                                        dealer.id === props.selectedDealerID,
                                })}
                            />
                            <span
                                className={classNames({
                                    [listStyles.pinNumber]: true,
                                    [listStyles.selectedPinNumber]:
                                        dealer.id === props.selectedDealerID,
                                    [listStyles.featuredPinNumber]:
                                        dealer.is_featured_emtek,
                                })}
                            >
                                {i + 1}
                            </span>
                        </div>
                        <div className={listStyles.contactInfo}>
                            <Clickable
                                unstyled={true}
                                className={listStyles.title}
                            >
                                {dealer.name}
                            </Clickable>
                            <p className={listStyles.details}>
                                {[dealer.address, dealer.address2]
                                    .filter(Boolean)
                                    .join(" ")}
                                , {dealer.city}, {dealer.state} {dealer.postal}
                            </p>
                            {dealer.phone && (
                                <p className={listStyles.details}>
                                    <Clickable
                                        unstyled
                                        href={`tel: ${dealer.phone}`}
                                    >
                                        {formatPhoneNumber(dealer.phone)}
                                    </Clickable>
                                </p>
                            )}
                            {attributes.length > 0 && (
                                <p className={listStyles.attributes}>
                                    {attributes.join(", ")}
                                </p>
                            )}
                        </div>
                        <div className={listStyles.directionsContainer}>
                            {dealer.distance && (
                                <span className={listStyles.distance}>
                                    {dealer.distance
                                        ? metersToMiles(dealer.distance)
                                        : ""}{" "}
                                    mi
                                </span>
                            )}
                            <Clickable
                                unstyled={true}
                                href={buildDirectionsURI(dealer)}
                                target="_blank"
                                className={`${listStyles.directionsButton} al-dealers__store-directions`}
                            >
                                <Directions className={listStyles.directions} />
                                <VisuallyHidden>Directions</VisuallyHidden>
                            </Clickable>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};

interface MarkerOptions {
    lat: number;
    lng: number;
    dealerListNumber: number;
    dealerID: number;
    selectedDealerID: number;
    isFeatured: boolean;
    setSelectedDealerState: (id: number) => void;
}

export const Marker = (props: MarkerOptions) => {
    const onMarkerClick = () => {
        props.setSelectedDealerState(props.dealerID);
    };

    const isSelected = props.selectedDealerID === props.dealerID;

    const PinComponent = props.isFeatured ? FeaturedPin : Pin;

    const pinAnalyticsClasses = props.isFeatured
        ? "al-dealers__map-pin al-dealers__map-pin--featured"
        : "al-dealers__map-pin";

    return (
        <div
            className={classNames(
                mapStyles.mapMarker,
                props.isFeatured && mapStyles.featuredPin,
                pinAnalyticsClasses,
            )}
            onClick={onMarkerClick}
            data-id={props.dealerID}
        >
            {isSelected && (
                <>
                    <PinComponent
                        className={classNames({
                            [mapStyles.selectedPin]: true,
                        })}
                    />
                    <span
                        className={classNames({
                            [mapStyles.pinNumber]: true,
                            [mapStyles.selectedPinNumber]: true,
                        })}
                    >
                        {props.dealerListNumber}
                    </span>
                </>
            )}
            {!isSelected && <div className={mapStyles.circleMarker} />}
        </div>
    );
};

interface DealerMapProps {
    dealers: Dealer[];
    selectedDealerID: number;
    setSelectedDealerState: (id: number) => void;
}

export const DealerMap = (props: DealerMapProps) => {
    const router = useRouterParams({
        shallow: false,
        method: "push",
        scroll: false,
    });
    const defaultCenter = { lat: 34.09741, lng: -117.968269 }; // Emtek Headquarters Zip Code Coords
    const center = props.dealers[0]
        ? { lat: props.dealers[0].latitude, lng: props.dealers[0].longitude }
        : defaultCenter;
    // Needed to ensure the server render perfectly matches the first client render

    const mapRef = useRef<google.maps.Map>();

    useEffect(() => {
        const selectedDealer = props.dealers.find(
            (dealer) => dealer.id === props.selectedDealerID,
        );
        const map = mapRef.current;
        if (map && selectedDealer) {
            map.setCenter({
                lat: selectedDealer.latitude,
                lng: selectedDealer.longitude,
            });
        }
        // Even if the dealers list changes, selectedDealerID will still point to a dealer
        // with the same details so it's safe not to re-run this effect
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedDealerID]);

    const onSearchThisArea = () => {
        const map = mapRef.current;
        if (!map) return null;
        const bounds = map.getBounds();
        if (!bounds) return null;
        const center = bounds.getCenter();
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const radius = metersToMiles(
            google.maps.geometry.spherical.computeDistanceBetween(ne, sw) / 2,
        );

        const queryParams = {
            q: `${center.lat().toString()},${center.lng().toString()}`,
            distance: `${radius.toString()}`,
        };
        router.setParams(queryParams);
    };

    if (!useIsClient()) return null;
    return (
        <GoogleMapReact
            bootstrapURLKeys={{ key: googleApiKey, libraries: "geometry" }}
            defaultCenter={defaultCenter}
            center={center}
            defaultZoom={10}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={({ map }: { map: google.maps.Map }) => {
                mapRef.current = map;

                const controlButton = document.createElement("button");
                controlButton.classList.add(styles.searchThisAreaButton);
                controlButton.innerText = "Search This Area";
                map.controls[google.maps.ControlPosition.TOP_CENTER].push(
                    controlButton,
                );

                controlButton.addEventListener("click", onSearchThisArea);
            }}
            options={{ mapId: "7cb0e2bbfc0a6110" }}
        >
            {props.dealers.map((dealer: Dealer, i: number) => (
                <Marker
                    key={dealer.id}
                    lat={dealer.latitude}
                    lng={dealer.longitude}
                    dealerListNumber={i + 1}
                    dealerID={dealer.id}
                    selectedDealerID={props.selectedDealerID}
                    isFeatured={dealer.is_featured_emtek}
                    setSelectedDealerState={props.setSelectedDealerState}
                />
            ))}
        </GoogleMapReact>
    );
};

interface DealerFiltersProps {
    filterState: string;
    setFilterState: (filterState: string) => void;
    showOnlineDealers: boolean;
}

export const DealerFilters = (props: DealerFiltersProps) => {
    const onFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        props.setFilterState(e.target.value);
    };

    return (
        <form className={headerStyles.filtersContainer}>
            <label
                htmlFor="filter-closest-5"
                className={classNames({
                    [headerStyles.filter]: true,
                    [headerStyles.selectedFilter]:
                        props.filterState === "Closest 5",
                    "al-dealers__tab": true,
                    "al-dealers__tab--closest-five": true,
                })}
            >
                <input
                    id="filter-closest-5"
                    type="radio"
                    name="filter"
                    value="Closest 5"
                    checked={props.filterState === "Closest 5"}
                    onChange={onFilterChange}
                />
                <MapWithPins className={headerStyles.map} />
                Closest 5
            </label>
            <label
                htmlFor="filter-all"
                className={classNames({
                    [headerStyles.filter]: true,
                    [headerStyles.selectedFilter]:
                        props.filterState === "All Locations",
                    "al-dealers__tab": true,
                    "al-dealers__tab--all-locations": true,
                })}
            >
                <input
                    id="filter-all"
                    type="radio"
                    name="filter"
                    value="All Locations"
                    checked={props.filterState === "All Locations"}
                    onChange={onFilterChange}
                />
                <Globe className={headerStyles.globe} />
                All Locations
            </label>

            <label
                htmlFor="filter-featured"
                className={classNames({
                    [headerStyles.filter]: true,
                    [headerStyles.selectedFilter]:
                        props.filterState === "Featured",
                    "al-dealers__tab": true,
                    "al-dealers__tab--featured": true,
                })}
            >
                <input
                    id="filter-featured"
                    type="radio"
                    name="filter"
                    value="Featured"
                    checked={props.filterState === "Featured"}
                    onChange={onFilterChange}
                />
                <FeaturedStar className={headerStyles.star} />
                Top Tier Display Dealer
            </label>

            {props.showOnlineDealers && (
                <label
                    htmlFor="filter-dealers"
                    className={classNames({
                        [headerStyles.filter]: true,
                        [headerStyles.selectedFilter]:
                            props.filterState === "Dealers",
                        "al-dealers__tab": true,
                        "al-dealers__tab--featured": true,
                    })}
                >
                    <input
                        id="filter-dealers"
                        type="radio"
                        name="filter"
                        value="Dealers"
                        checked={props.filterState === "Dealers"}
                        onChange={onFilterChange}
                    />
                    <DealerCart className={headerStyles.star} />
                    Online Dealers
                </label>
            )}
        </form>
    );
};

export const DealerFiltersDropdown = (props: DealerFiltersProps) => {
    const onDropdownChange = (value: string) => {
        props.setFilterState(value);
    };

    return (
        <Dropdown
            onChange={onDropdownChange}
            labelSize="small"
            defaultValue="All Locations"
        >
            <ListboxOption value="Closest 5">Closest 5</ListboxOption>
            <ListboxOption value="All Locations">All Locations</ListboxOption>
            <ListboxOption value="Featured">
                Top Tier Display Dealer
            </ListboxOption>
            <ListboxOption value="Dealers">Online Dealers</ListboxOption>
        </Dropdown>
    );
};

export const DealerHeader = (props: DealerFiltersProps) => {
    return (
        <header className={headerStyles.container}>
            <h5 className={headerStyles.title}>Quick Search</h5>
            {!useIsMobile() && (
                <DealerFilters
                    filterState={props.filterState}
                    setFilterState={props.setFilterState}
                    showOnlineDealers={props.showOnlineDealers}
                />
            )}
            {useIsMobile() && (
                <DealerFiltersDropdown
                    filterState={props.filterState}
                    setFilterState={props.setFilterState}
                    showOnlineDealers={props.showOnlineDealers}
                />
            )}
        </header>
    );
};

interface DealerLocatorProps {
    dealers: Dealer[];
    onlineDealers?: OnlineDealersBlock;
}

export const DealerLocator = ({
    dealers,
    onlineDealers,
}: DealerLocatorProps) => {
    const [filterState, setFilterState] = useState("Closest 5");
    const [selectedDealerState, setSelectedDealerState] = useState(
        dealers[0] ? dealers[0].id : 0,
    );
    const [numberOfDealersState, setNumberOfDealersState] = useState(5);

    const router = useRouterParams({
        shallow: false,
        method: "push",
        scroll: false,
    });

    const windowWidth = useWindowWidth();

    useEffect(() => {
        // on larger screensizes, we want to show all Dealers
        if (
            typeof windowWidth === "number" &&
            windowWidth > parseInt(sassVariables.breakpointMobile)
        ) {
            setNumberOfDealersState(dealers.length);
        }
    }, [windowWidth, dealers.length]);

    const onInputFieldChange = (searchString: string) => {
        router.setParams({ q: searchString, distance: "" });
    };

    const onDropdownChange = (distanceString: string) => {
        router.setParam("distance", distanceString);
    };

    const onShowMoreClick = useCallback(() => {
        setNumberOfDealersState(numberOfDealersState + 5);
    }, [numberOfDealersState]);

    const siteName = process.env.NEXT_PUBLIC_SITE_THEME
        ? String(process.env.NEXT_PUBLIC_SITE_THEME).charAt(0).toUpperCase() +
          String(process.env.NEXT_PUBLIC_SITE_THEME).slice(1)
        : "";

    function filterDealers(dealer: Dealer, i: number) {
        switch (filterState) {
            case "All Locations":
                return true;
            case "Closest 5":
                if (i < 5) {
                    return true;
                } else {
                    return false;
                }
            case "Featured":
                return dealer.is_featured_emtek;
        }
    }

    const showJumpToList = useIsMobile() && dealers.length > 0;

    const filterTitle = () => {
        switch (filterState) {
            case "Featured":
                return "Top Tier Display Dealers";
            case "Dealers":
                return "Online Retailers";
            default:
                return filterState;
        }
    };

    const onlineDealersContent = onlineDealers ? (
        <div className={styles.onlineDealersContainer}>
            {onlineDealers.value.map(
                (onlineDealer: OnlineDealerBlock, index: number) => {
                    return (
                        <OptionalCta
                            className={styles.onlineDealersImage}
                            key={index}
                            cta={{
                                link: onlineDealer.value.link,
                                text: onlineDealer.value.title,
                            }}
                            target={"_blank"}
                        >
                            <CmsResponsiveImage
                                cmsImage={onlineDealer.value.logo}
                                cldSrcSet={[
                                    400, 600, 800, 1000, 1200, 1400, 1600,
                                ]}
                                sizes="(max-width: 1024px) 100vw, (min-width: 768px) 30vw"
                            />
                        </OptionalCta>
                    );
                },
            )}
        </div>
    ) : (
        <></>
    );

    return (
        <div className={styles.grid}>
            <DealerHeader
                filterState={filterState}
                setFilterState={setFilterState}
                showOnlineDealers={onlineDealers ? true : false}
            />
            <div className={styles.title}>
                <h4>{filterTitle()}</h4>
                <p
                    className={styles.subtitle}
                    hidden={filterState !== "Featured"}
                >
                    Dealers who offer a broad selection of {siteName} products
                    on display. They are recognized for their product knowledge
                    and high level of customer satisfaction.
                </p>
            </div>
            {filterState !== "Dealers" ? (
                <>
                    <form
                        className={styles.searchForm}
                        onSubmit={(e) => {
                            e.preventDefault();
                        }}
                    >
                        <div>
                            <FormLabel htmlFor="locator-search" size="small">
                                Enter zip code
                            </FormLabel>
                            <SearchInput
                                id="locator-search"
                                className="al-dealers__zip-search"
                                submit={(value) => onInputFieldChange(value)}
                                placeholder="Example: 91706"
                            />
                        </div>
                        <div>
                            <Dropdown
                                className="al-dealers__distance"
                                onChange={(value) => onDropdownChange(value)}
                                labelSize="small"
                                defaultValue="25"
                                label="Select distance"
                            >
                                <ListboxOption value="25">
                                    25 miles
                                </ListboxOption>
                                <ListboxOption value="50">
                                    50 miles
                                </ListboxOption>
                                <ListboxOption value="75">
                                    75 miles
                                </ListboxOption>
                            </Dropdown>
                        </div>
                    </form>
                    {showJumpToList && (
                        <ButtonText
                            className={styles.jumpButton}
                            href="#dealer-list"
                        >
                            Jump to the List
                        </ButtonText>
                    )}

                    <DealerList
                        dealers={dealers
                            .filter(filterDealers)
                            .slice(0, numberOfDealersState)}
                        selectedDealerID={selectedDealerState}
                        setSelectedDealerID={setSelectedDealerState}
                    />
                    <div className={styles.dealerMap}>
                        <DealerMap
                            dealers={dealers
                                .filter(filterDealers)
                                .slice(0, numberOfDealersState)}
                            selectedDealerID={selectedDealerState}
                            setSelectedDealerState={(id) => {
                                // When clicking a map pin, also scroll the newly chosen dealer into view
                                const item = document.getElementById(
                                    `dealer-list-item-${id}`,
                                );
                                if (item) {
                                    item.scrollIntoView({
                                        behavior: "smooth",
                                        block: "nearest",
                                        inline: "nearest",
                                    });
                                }

                                setSelectedDealerState(id);
                            }}
                        />
                    </div>
                </>
            ) : (
                onlineDealersContent
            )}
            {useIsMobile() &&
                numberOfDealersState < dealers.length &&
                filterState !== "Dealers" && (
                    <ButtonPrimary
                        className={styles.showMoreButton}
                        onClick={onShowMoreClick}
                    >
                        Show More
                    </ButtonPrimary>
                )}
        </div>
    );
};
